import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useId, useState } from 'react';
import { COUNT_PLACEHOLDER } from '../../constants/aiScanner';
import chevronDownIcon from '../../icons/Icon-Chevron-Down-Green.svg';
import {
  trackHashtagClear,
  trackHashtagCommit,
  trackHashtagToggle
} from '../../utils/aiScannerMetrics';
import useLocalize from '../../utils/hooks/useLocalize';
import InlineErrorMessage from '../common/InlineErrorMessage';

const renderSubmitCountChunk = (chunks) => (
  <span className="hashtag-chips-action-bar-submit-count">{chunks}</span>
);

/**
 * Hashtag definitions for the AI Scanner.
 *
 * Each entry is structured to mirror how preset (static) scans work in the
 * project: an `id` that will map to a backend scan configuration, and a
 * `labelKey` for localization. When the real-time wiring (STORY-008) is
 * connected, each hashtag's `id` will be sent to the scan API to retrieve
 * matching results — similar to how `staticScans` are fetched and applied via
 * `state.scans.staticScans` in the existing desktop scanner flow.
 */
export const HASHTAG_DEFINITIONS = [
  { id: 'daily_life_companies', labelKey: 'ai.scanner.hashtag.chip.1' },
  { id: 'elite_management', labelKey: 'ai.scanner.hashtag.chip.2' },
  { id: 'passive_income', labelKey: 'ai.scanner.hashtag.chip.3' },
  { id: 'affordable_stocks', labelKey: 'ai.scanner.hashtag.chip.4' },
  { id: 'bonus_adventure', labelKey: 'ai.scanner.hashtag.chip.5' },
  { id: 'counterattack_from_bottom', labelKey: 'ai.scanner.hashtag.chip.6' },
  { id: 'potential_big_breakout', labelKey: 'ai.scanner.hashtag.chip.7' },
  { id: 'far_from_target_price', labelKey: 'ai.scanner.hashtag.chip.8' },
  { id: 'pro_approved_attention', labelKey: 'ai.scanner.hashtag.chip.9' },
  { id: 'treasure_hunting_undervalued', labelKey: 'ai.scanner.hashtag.chip.10' },
  { id: 'unstoppable_momentum', labelKey: 'ai.scanner.hashtag.chip.11' },
  { id: 'fast_inventory_capital_cycling', labelKey: 'ai.scanner.hashtag.chip.12' },
];

/** Number of chips visible before "show more" is tapped, by viewport height. */
const INITIAL_VISIBLE_SMALL = 6; // height < 667px
const INITIAL_VISIBLE_DEFAULT = 8; // height >= 667px

/** Stable identity so an omitted `selectedIds` does not defeat `memo`. */
const EMPTY_SELECTION = new Set();

/**
 * Returns the number of chips to show initially based on viewport height.
 */
const getInitialVisibleCount = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(max-height: 666px)').matches
      ? INITIAL_VISIBLE_SMALL
      : INITIAL_VISIBLE_DEFAULT;
  }
  return INITIAL_VISIBLE_DEFAULT;
};

/**
 * HashtagChips Component
 *
 * Renders a grid of selectable hashtag pill-shaped chips inside the AI Scanner
 * bottom sheet. Chips toggle between selected (green) and unselected (grey)
 * states on tap.
 *
 * Initially shows 6 chips (<667px screens) or 8 chips (≥667px screens).
 * Tapping "さらに表示" reveals the remaining chips.
 *
 * The action bar at the bottom provides "clear all" and "show results" controls.
 * Live match-count filtering is deferred to STORY-008; a static placeholder
 * count ("- 件") is shown for now.
 *
 * Controlled: the selection is owned by AiScannerContainer and arrives as
 * `selectedIds`. A local copy would let the chips disagree with the container
 * whenever the container resets the selection without unmounting this
 * component — which is what `handleResume` does when a member picks
 * 「前回の履歴を見る」 with the sheet already on screen. (#739 reached here the
 * same way by clearing on re-open; STORY-029 (#740) stopped clearing there, so
 * that is no longer an example, but the ownership rule it motivated stands.)
 *
 * @param {Object} props
 * @param {Set<string>} [props.selectedIds] - The currently selected hashtag IDs.
 * @param {Function} [props.onShowResults] - Called when the "show results" button is pressed.
 *   Will be wired in STORY-008. Receives the array of selected hashtag IDs.
 * @param {Function} [props.onSelectionChange] - Called whenever the set of
 *   selected hashtag IDs changes (toggle or clear). Receives the new Set of IDs.
 *   Used by AiScannerContainer to fire de-duplicated scan requests (STORY-007).
 * @param {number|null} [props.matchCount] - STORY-008: live count of matching
 *   symbols for the current selection. `null` indicates no count is available
 *   yet (loading or error) and renders the "-" placeholder. #737: a `0`
 *   against a non-empty selection also shows the "use fewer hashtags" error.
 * @param {boolean} [props.isLoading] - STORY-008: true while any required scan
 *   is still being fetched.
 * @param {boolean} [props.hasError] - STORY-008: true when at least one required
 *   scan has errored; disables the submit button. #737: also shows the
 *   "failed to load" error, distinct from the zero-result one above.
 */
const HashtagChips = ({
  selectedIds = EMPTY_SELECTION,
  onShowResults,
  onSelectionChange,
  matchCount = null,
  isLoading = false,
  hasError = false
}) => {
  const localize = useLocalize();
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(getInitialVisibleCount);
  // #737: what the chip group's `aria-describedby` points at. From `useId`
  // rather than a literal so a second mounted sheet cannot collide on it.
  const errorId = useId();

  // Update visible count on viewport height changes (e.g. rotation)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-height: 666px)');
    const handleChange = (e) => {
      if (!expanded) {
        setVisibleCount(e.matches ? INITIAL_VISIBLE_SMALL : INITIAL_VISIBLE_DEFAULT);
      }
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [expanded]);

  const handleChipToggle = (id) => {
    const isSelecting = !selectedIds.has(id);
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange?.(next);
    // #731: fire-and-forget telemetry for each select/unselect toggle.
    trackHashtagToggle({
      hashtagId: id,
      action: isSelecting ? 'select' : 'unselect',
      selectedCount: next.size
    });
  };

  const handleClearAll = () => {
    const cleared = Array.from(selectedIds);
    onSelectionChange?.(new Set());
    // #731: fire-and-forget telemetry — one "clear" event for the cleared set.
    if (cleared.length > 0) {
      trackHashtagClear({ hashtagIds: cleared });
    }
  };

  const handleShowResults = () => {
    // #731: fire-and-forget telemetry for the committed hashtag set. `matchCount`
    // is the NNN shown on the 「NNN 件を表示」 button at tap time. Guard on a
    // non-empty selection (like handleClearAll) so a commit event always carries
    // >= 1 id, matching the data-model invariant even if this is ever reached
    // with an empty set (e.g. the disabled condition changes).
    if (selectedIds.size > 0) {
      trackHashtagCommit({
        hashtagIds: Array.from(selectedIds),
        selectedCount: selectedIds.size,
        resultCount: matchCount
      });
    }
    if (onShowResults) {
      onShowResults(Array.from(selectedIds));
    }
  };

  const handleShowMore = useCallback(() => {
    setExpanded(true);
  }, []);

  const chipsToRender = expanded
    ? HASHTAG_DEFINITIONS
    : HASHTAG_DEFINITIONS.slice(0, visibleCount);

  const hasMoreChips = !expanded && visibleCount < HASHTAG_DEFINITIONS.length;

  // #737 AC C20108247: the selection has narrowed the pool to nothing, so the
  // member is asked to drop a hashtag rather than left looking at a live
  // 「0 件を表示」. Gated on a non-empty selection: `filterResult` reports 0 for
  // an empty one too (see AiScannerContainer), and an untouched sheet has not
  // narrowed anything — the submit button shows 「- 件を表示」 there.
  //
  // `isLoading` and `hasError` are excluded so the box never contradicts the
  // count beside it: both arrive with a `null` count, which is the `-`
  // placeholder, not a zero.
  const hasZeroResults =
    !hasError && !isLoading && selectedIds.size > 0 && matchCount === 0;

  // #737 AC C20108248: a scan that failed or came back with no data — the
  // distinction the AC exists to draw, so the two messages must never both
  // apply.
  //
  // That exclusion is enforced HERE rather than trusted from upstream.
  // `filterResult` does null the count whenever it reports an error, so the
  // two cannot co-occur anyway, but `!hasError` inside `hasZeroResults` and
  // the `else` below each hold on their own without it. The redundancy is
  // deliberate on both counts: the flag stays correct if it is ever read
  // outside this chain, and the `else` keeps the precedence — a load failure
  // outranks a zero — visible at the point it is decided.
  let errorMessageKey = null;
  if (hasError) {
    errorMessageKey = 'ai.scanner.hashtag.error.load.failed';
  } else if (hasZeroResults) {
    errorMessageKey = 'ai.scanner.hashtag.error.narrowed.to.zero';
  }

  const countLabel =
    selectedIds.size === 0 || matchCount == null
      ? COUNT_PLACEHOLDER
      : String(matchCount);

  return (
    <div className="hashtag-chips">
      {/*
        Above the scroll area rather than inside it: the box explains the state
        of the whole selection, so it has to stay put under the sheet title
        while the member scrolls the chips looking for one to drop.
      */}
      {errorMessageKey && (
        <InlineErrorMessage
          className="hashtag-chips-error"
          id={errorId}
          message={localize(errorMessageKey)}
        />
      )}
      <div className="hashtag-chips-scroll">
        {/*
          #737: the error describes the chip GROUP, not the submit button.
          Both error states disable that button, and a disabled button is not
          focusable, so `aria-describedby` on it would never be announced.
          The chips are what the member has to act on in both cases — drop one
          to widen the selection, or re-tap one to retry the failed scan — so
          the group is where the guidance belongs.
        */}
        <div
          className="hashtag-chips-grid"
          role="group"
          aria-label={localize('ai.scanner.hashtag.modal.title')}
          aria-describedby={errorMessageKey ? errorId : undefined}
        >
          {chipsToRender.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`hashtag-chip${selectedIds.has(tag.id) ? ' hashtag-chip--selected' : ''}`}
              onClick={() => handleChipToggle(tag.id)}
              aria-pressed={selectedIds.has(tag.id)}
            >
              {localize(tag.labelKey)}
            </button>
          ))}
        </div>
        {hasMoreChips && (
          <button
            type="button"
            className="hashtag-chips-show-more"
            aria-expanded={expanded}
            onClick={handleShowMore}
          >
            <span className="hashtag-chips-show-more-text">
              {localize('ai.scanner.hashtag.show.more')}
            </span>
            <img
              src={chevronDownIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
              className="hashtag-chips-show-more-chevron"
            />
          </button>
        )}
      </div>
      <div className="hashtag-chips-action-bar">
        <button
          type="button"
          className="hashtag-chips-action-bar-clear"
          onClick={handleClearAll}
          disabled={selectedIds.size === 0}
        >
          {localize('ai.scanner.hashtag.clear.all')}
        </button>
        <button
          type="button"
          className="hashtag-chips-action-bar-submit"
          onClick={handleShowResults}
          disabled={
            selectedIds.size === 0 ||
            isLoading ||
            hasError ||
            matchCount == null ||
            matchCount === 0
          }
        >
          {localize('ai.scanner.hashtag.show.results', {
            0: countLabel,
            b: renderSubmitCountChunk
          })}
        </button>
      </div>
    </div>
  );
};

HashtagChips.propTypes = {
  selectedIds: PropTypes.instanceOf(Set),
  onShowResults: PropTypes.func,
  onSelectionChange: PropTypes.func,
  matchCount: PropTypes.number,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool
};

// STORY-013 (#713): memoized. AiScannerContainer re-renders on every pointer
// move while the bottom sheet is dragged, and `onSelectionChange` now has a
// stable identity, so without this the whole chip grid re-renders at frame rate
// for props that never changed.
export default memo(HashtagChips);
